import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Mail, RefreshCw, Trash2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { EmailAccount } from '@/hooks/useEmailAccounts';

const providerConfig = {
  gmail: {
    name: 'Gmail',
    color: 'bg-red-500',
  },
  microsoft: {
    name: 'Microsoft 365',
    color: 'bg-blue-500',
  },
};

interface EmailAccountCardProps {
  account: EmailAccount;
  onSync: (emailAddress: string) => void;
  onDisconnect: (accountId: string) => void;
  isSyncing?: boolean;
}

export function EmailAccountCard({ 
  account, 
  onSync, 
  onDisconnect,
  isSyncing = false 
}: EmailAccountCardProps) {
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className={`p-2 ${providerConfig[account.provider as keyof typeof providerConfig]?.color || 'bg-primary'}/10 rounded-lg`}>
            <Mail className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-medium">{account.email_address}</h3>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={account.is_active ? 'default' : 'secondary'}>
                {providerConfig[account.provider as keyof typeof providerConfig]?.name || account.provider}
              </Badge>
              {account.last_sync_at && (
                <span className="text-sm text-muted-foreground">
                  Synced {formatDistanceToNow(new Date(account.last_sync_at), { addSuffix: true })}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onSync(account.email_address)}
            disabled={isSyncing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
            Sync
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDisconnect(account.id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
}
